import React from 'react';
import { T } from '../../theme/tokens';

// NDC maroon accent at different opacities
const A = T.accent;
const Adim = `${T.accent}99`;
const dim = T.borderLight;

export function Emblem({ kind, size = 84 }) {
  const common = { width: size, height: size, viewBox: "0 0 100 100", style: { flexShrink: 0 } };

  switch (kind) {
    case "evaluator": // scanning arc over dim points
      return (
        <svg {...common}>
          <circle cx="50" cy="58" r="34" fill="none" stroke={dim} strokeDasharray="2 5" />
          {[[34, 66], [50, 74], [66, 64], [58, 48], [40, 50]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3" fill={dim} />
          ))}
          <path d="M 50 58 L 22 36 A 36 36 0 0 1 78 36 Z" fill={A} opacity="0.16" />
          <path d="M 50 58 L 22 36" stroke={A} strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 50 58 L 78 36" stroke={A} strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="50" cy="58" r="5" fill={A} />
        </svg>
      );

    case "experimenter": // sparks scattering from one source
      return (
        <svg {...common}>
          <circle cx="50" cy="62" r="7" fill={A} />
          {[[20, 28], [38, 16], [62, 14], [80, 26], [88, 48], [14, 50]].map(
            ([x, y], i) => (
              <g key={i}>
                <line x1="50" y1="62" x2={x} y2={y} stroke={dim} strokeWidth="1.4" strokeDasharray="3 4" />
                <circle cx={x} cy={y} r="3.6" fill={i % 2 === 0 ? A : Adim} opacity="0.9" />
              </g>
            )
          )}
        </svg>
      );

    case "stalled": // power node, connections stop just short
      return (
        <svg {...common}>
          <rect x="38" y="38" width="24" height="24" rx="0" fill={A} opacity="0.9" />
          <rect x="38" y="38" width="24" height="24" rx="0" fill="none" stroke={A} />
          {[[50, 38, 50, 18], [62, 50, 84, 50], [50, 62, 50, 82], [38, 50, 16, 50]].map(
            ([x1, y1, x2, y2], i) => {
              const mx = x1 + (x2 - x1) * 0.55;
              const my = y1 + (y2 - y1) * 0.55;
              return (
                <g key={i}>
                  <line x1={x1} y1={y1} x2={mx} y2={my} stroke={Adim} strokeWidth="2.4" strokeLinecap="round" />
                  <circle cx={x2} cy={y2} r="4.4" fill="none" stroke={dim} strokeWidth="1.6" />
                </g>
              );
            }
          )}
        </svg>
      );

    case "quiet": // solid stacked base, unlit beacon
      return (
        <svg {...common}>
          {[[24, 72, 52], [30, 60, 40], [36, 48, 28]].map(([x, y, w], i) => (
            <rect key={i} x={x} y={y} width={w} height="10" rx="0" fill={A} opacity={0.35 + i * 0.2} />
          ))}
          <rect x="46" y="32" width="8" height="14" rx="0" fill={A} opacity="0.55" />
          <circle cx="50" cy="24" r="6" fill="none" stroke={A} strokeWidth="2.2" />
          <circle cx="50" cy="24" r="2.4" fill={A} opacity="0.5" />
        </svg>
      );

    case "primed": // drawn-back arrow on taut line
      return (
        <svg {...common}>
          <path d="M 26 22 Q 16 50 26 78" fill="none" stroke={Adim} strokeWidth="2.4" strokeLinecap="round" />
          <line x1="26" y1="22" x2="44" y2="50" stroke={dim} strokeWidth="1.4" />
          <line x1="26" y1="78" x2="44" y2="50" stroke={dim} strokeWidth="1.4" />
          <line x1="44" y1="50" x2="78" y2="50" stroke={A} strokeWidth="3" strokeLinecap="round" />
          <path d="M 78 50 L 68 44 M 78 50 L 68 56" stroke={A} strokeWidth="3" strokeLinecap="round" />
          <circle cx="44" cy="50" r="3.4" fill={A} />
        </svg>
      );

    case "scaler": // compounding bars with connecting thread
      return (
        <svg {...common}>
          {[[22, 62, 16], [40, 50, 28], [58, 36, 42]].map(([x, y, h], i) => (
            <rect key={i} x={x} y={y} width="12" height={h} rx="0" fill={A} opacity={0.4 + i * 0.25} />
          ))}
          <path
            d="M 28 58 L 46 46 L 64 32"
            fill="none"
            stroke={A}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeDasharray="1 5"
          />
          <circle cx="64" cy="32" r="3.6" fill={A} />
        </svg>
      );

    case "regrouper": // surge forward, deliberate loop back, off again
      return (
        <svg {...common}>
          <path
            d="M 14 64 C 34 64 40 64 52 64 C 70 64 70 42 54 42 C 40 42 42 58 56 56 C 70 54 76 44 86 36"
            fill="none"
            stroke={A}
            strokeWidth="2.6"
            strokeLinecap="round"
          />
          <path d="M 86 36 L 76 36 M 86 36 L 82 46" stroke={A} strokeWidth="2.6" strokeLinecap="round" />
          <circle cx="14" cy="64" r="3.4" fill={Adim} />
        </svg>
      );

    default:
      return null;
  }
}
